package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import com.advaita.cloud.drive.ui.api.PlainRecord
import com.advaita.cloud.drive.ui.store.FileBrowserStore
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement

@Composable
fun UploadProgress(store: FileBrowserStore) {
    // The Resume panel and any in-flight resume rows render regardless of whether
    // a fresh batch upload is running, so it must live OUTSIDE the batch-counter
    // early-return below.
    ResumePanel(store)

    val totalBatch = store.inFlightUploads
    val active = store.activeUploads
    // Nothing to show if there's neither a batch in flight nor any active row
    // (a lone resume run has totalBatch == 0 but a non-empty active list).
    if (totalBatch == 0 && active.isEmpty()) return
    val doneBatch = store.completedUploads

    Div(attrs = { classes("upload-progress") }) {
        // The batch header only applies to a fresh multi-file upload; a lone
        // resume run (totalBatch == 0) just shows its per-file row.
        if (totalBatch > 0) {
            val folderName = store.currentUploadFolderName
            val header = if (folderName != null) {
                "Uploading folder \"$folderName\" — $doneBatch of $totalBatch done, ${active.size} in flight"
            } else {
                "Uploading — $doneBatch of $totalBatch done, ${active.size} in flight"
            }
            Text(header)
        }
        if (active.isEmpty()) return@Div
        active.forEach { p ->
            // Suppress byte/rate/ETA while the orchestrator is in its warmup
            // window — sockets race wildly during slow-start and the aggregate
            // bytes / rate / ETA jitter is misleading. The orchestrator flips
            // `inWarmup` to false once a part confirms OR the 60s window
            // expires, whichever comes first.
            val showReal = !p.inWarmup
            val percent: Int = if (showReal && p.totalBytes > 0L) {
                ((p.bytesDone.toDouble() / p.totalBytes.toDouble()) * 100).toInt().coerceIn(0, 100)
            } else 0

            Div(attrs = { classes("upload-progress-row") }) {
                if (!showReal) {
                    Text("${p.name} — initializing…")
                } else {
                    val partsLabel = if (p.partsTotal != null) {
                        " — uploading: ${p.partsInFlight ?: 0}/${p.partsTotal}; completed: ${p.partsDone ?: 0}/${p.partsTotal}"
                    } else ""
                    val byteLabel = if (p.totalBytes > 0L) " — ${formatBytes(p.bytesDone)} / ${formatBytes(p.totalBytes)} ($percent%)" else ""
                    val rateLabel = p.bytesPerSecond?.let { " — ${formatRate(it)}" } ?: ""
                    val etaLabel = p.etaSeconds?.let { " — ETA ${formatDuration(it)}" } ?: ""
                    Text("${p.name}$partsLabel$byteLabel$rateLabel$etaLabel")
                    if (p.totalBytes > 0L) {
                        Div(attrs = { classes("upload-progress-bar") }) {
                            Div(attrs = {
                                classes("upload-progress-fill")
                                style { property("width", "$percent%") }
                            }) {}
                        }
                    }
                }
            }
        }
    }
}

/**
 * The Resume panel: one row per server-validated resumable session, plus any
 * identity-mismatch error. Renders nothing when there are no candidates and no
 * error, so it's cheap to always compose.
 */
@Composable
private fun ResumePanel(store: FileBrowserStore) {
    val candidates = store.resumableCandidates
    val error = store.resumeError
    if (candidates.isEmpty() && error == null) return

    Div(attrs = { classes("upload-progress", "resume-panel") }) {
        error?.let {
            Div(attrs = { classes("resume-error") }) { Text(it) }
        }
        if (candidates.size == 1) {
            // Single file: message and actions share one vertically-centered row
            // (message left, buttons right — aligned on the row's horizontal centerline).
            val rec = candidates[0]
            Div(attrs = { classes("upload-progress-row", "resume-row") }) {
                Span(attrs = { classes("resume-msg") }) {
                    // The filename always stays visible; only the folder path
                    // truncates (ellipsis) when the row runs out of room. Single
                    // line, no wrap.
                    Span(attrs = { classes("resume-fixed") }) { Text("We detected an interrupted upload on file: ") }
                    Span(attrs = { classes("resume-fixed") }) { Text(rec.identity.name) }
                    Span(attrs = { classes("resume-fixed") }) { Text(" → ") }
                    Span(attrs = { classes("resume-path") }) { Text(rec.destinationLabel) }
                    Span(attrs = { classes("resume-fixed") }) { Text(", would you like us to resume that upload?") }
                }
                ResumeActions(store, rec)
            }
        } else if (candidates.isNotEmpty()) {
            // Several files: the message lists names, then one labelled row per file
            // so each row's buttons stay attributable.
            val names = candidates.joinToString(", ") { it.identity.name }
            Div(attrs = { classes("resume-title") }) {
                Text(
                    "We detected an interrupted upload on several files: [$names], " +
                        "would you like us to resume those uploads?",
                )
            }
            candidates.forEach { rec ->
                Div(attrs = { classes("upload-progress-row", "resume-row") }) {
                    Span(attrs = { classes("resume-name") }) { Text("${rec.identity.name} → ${rec.destinationLabel}") }
                    ResumeActions(store, rec)
                }
            }
        }
    }
}

@Composable
private fun ResumeActions(store: FileBrowserStore, rec: PlainRecord) {
    Button(attrs = {
        classes("btn")
        onClick { openResumeFilePicker(store, rec) }
    }) { Text("Resume") }
    Button(attrs = {
        classes("btn")
        onClick { store.discardResumable(rec) }
    }) { Text("Discard") }
}

/**
 * Single-file hidden-input picker for resuming [rec]. Mirrors Header.openFilePicker,
 * minus `multiple`: the user re-selects exactly the one interrupted file. Identity
 * matching happens in [FileBrowserStore.resumeUpload].
 */
private fun openResumeFilePicker(store: FileBrowserStore, rec: PlainRecord) {
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.style.display = "none"
    // Narrow the native picker to the original file's type. Browsers forbid
    // pre-filling an exact filename, but `accept` filters by extension (and
    // MIME, when we recorded one) so unrelated files grey out — the recorded
    // identity is still the authoritative check in resumeUpload. Extension is
    // the more reliable hint across OS dialogs, so lead with it.
    val ext = rec.identity.name.substringAfterLast('.', "")
    val accept = buildList {
        if (ext.isNotEmpty()) add(".$ext")
        rec.mimeType?.takeIf { it.isNotBlank() }?.let { add(it) }
    }.joinToString(",")
    if (accept.isNotEmpty()) input.accept = accept
    input.onchange = {
        val file = input.files?.item(0)
        if (file != null) store.resumeUpload(rec, file)
        input.remove()
        Unit
    }
    document.body?.appendChild(input)
    input.click()
}

private fun formatBytes(bytes: Long): String {
    val kib = 1024L
    val mib = kib * 1024
    val gib = mib * 1024
    return when {
        bytes >= gib -> "${(bytes.toDouble() / gib * 100).toInt() / 100.0} GiB"
        bytes >= mib -> "${(bytes.toDouble() / mib * 10).toInt() / 10.0} MiB"
        bytes >= kib -> "${bytes / kib} KiB"
        else -> "$bytes B"
    }
}

private fun formatRate(bytesPerSecond: Double): String {
    val mibps = bytesPerSecond / 1_048_576.0
    return if (mibps >= 1.0) {
        "${(mibps * 10).toInt() / 10.0} MiB/s"
    } else {
        "${(bytesPerSecond / 1024.0).toInt()} KiB/s"
    }
}

private fun formatDuration(seconds: Long): String {
    if (seconds < 0) return "—"
    val h = seconds / 3600
    val m = (seconds % 3600) / 60
    val s = seconds % 60
    return when {
        h > 0 -> "${h}h ${m}m"
        m > 0 -> "${m}m ${s}s"
        else -> "${s}s"
    }
}
